import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ohcampus_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('ohcampus_token');
      localStorage.removeItem('ohcampus_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Filters API
export const filtersAPI = {
  getAll: () => api.get('/filters'),
  getCitiesByState: (state) => api.get('/filters/cities', { params: { state } }),
};

// Colleges API
export const collegesAPI = {
  getAll: (params) => api.get('/colleges', { params }),
  getById: (id) => api.get(`/colleges/${id}`),
  compare: (collegeIds) => api.get(`/colleges/compare`, { params: { college_ids: collegeIds } }),
  create: (data) => api.post('/colleges', data),
  update: (id, data) => api.put(`/colleges/${id}`, data),
  getFeeSummary: (collegeId) => api.get(`/colleges/${collegeId}/fee-summary`),
  updateAdmissionAlerts: (id, alerts) => api.put(`/colleges/${id}/admission-alerts`, alerts),
};

// Courses API
export const coursesAPI = {
  getByCollege: (collegeId) => api.get(`/colleges/${collegeId}/courses`),
  getAll: (params) => api.get('/courses', { params }),
  getAllWithCollege: (params) => api.get('/courses/with-college', { params }),
  getById: (courseId) => api.get(`/courses/${courseId}`),
  getCategories: () => api.get('/courses/categories/list'),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  updateSeatStatus: (id, seatStatus) => api.put(`/courses/${id}/seat-status`, { seat_status: seatStatus }),
};

// Placements API
export const placementsAPI = {
  getByCollege: (collegeId) => api.get(`/colleges/${collegeId}/placements`),
  update: (collegeId, data) => api.put(`/colleges/${collegeId}/placements`, data),
};

// Fees API
export const feesAPI = {
  getAll: (params) => api.get('/fees', { params }),
  getByCollege: (collegeId) => api.get(`/colleges/${collegeId}/fees`),
  create: (data) => api.post('/fees', data),
  createBulk: (data) => api.post('/fees/bulk', data),
  update: (id, data) => api.put(`/fees/${id}`, data),
  delete: (id) => api.delete(`/fees/${id}`),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/fees/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getCSVTemplate: () => api.get('/fees/csv-template'),
};

// Admission Charges API
export const admissionChargesAPI = {
  getAll: (params) => api.get('/admission-charges', { params }),
  getByCollege: (collegeId) => api.get(`/colleges/${collegeId}/admission-charges`),
  create: (data) => api.post('/admission-charges', data),
  update: (id, data) => api.put(`/admission-charges/${id}`, data),
  delete: (id) => api.delete(`/admission-charges/${id}`),
};

// FAQs API
export const faqsAPI = {
  getAll: (params) => api.get('/faqs', { params }),
  create: (data) => api.post('/faqs', data),
  update: (id, data) => api.put(`/faqs/${id}`, data),
  delete: (id) => api.delete(`/faqs/${id}`),
};

// User Management API (Admin only)
export const usersAPI = {
  getAll: () => api.get('/admin/users'),
  getById: (id) => api.get(`/admin/users/${id}`),
  getTeamLeads: () => api.get('/admin/users/team-leads'),
  getDesignations: () => api.get('/admin/designations'),
  create: (data) => api.post('/admin/users', data),
  update: (id, data) => api.put(`/admin/users/${id}`, data),
  delete: (id) => api.delete(`/admin/users/${id}`),
  resetPassword: (id, newPassword) => api.put(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
  setPassword: (id, newPassword) => api.put(`/admin/users/${id}/set-password`, { new_password: newPassword }),
};

// Profile API (Current user)
export const profileAPI = {
  get: () => api.get('/profile'),
  update: (data) => api.put('/profile', data),
  changePassword: (currentPassword, newPassword) => api.put('/profile/password', { current_password: currentPassword, new_password: newPassword }),
};

// Activity Logs API (Admin only)
export const activityAPI = {
  getAll: (params) => api.get('/admin/activity-logs', { params }),
  getActions: () => api.get('/admin/activity-logs/actions'),
};

// Admissions API
export const admissionsAPI = {
  getAll: () => api.get('/admissions'),
  getById: (id) => api.get(`/admissions/${id}`),
  create: (data) => api.post('/admissions', data),
  update: (id, data) => api.put(`/admissions/${id}`, data),
  delete: (id) => api.delete(`/admissions/${id}`),
  reassignCounselor: (id, counselor_id, reason) =>
    api.patch(`/admissions/${id}/counselor`, { counselor_id, reason: reason || null }),
};

// Performance Stats API (Admin and Manager)
export const statsAPI = {
  getPerformance: () => api.get('/admin/stats/performance'),
  getAdmissionsList: (params) => api.get('/admin/stats/admissions-list', { params }),
  getScholarshipSummary: (viewBy) => api.get('/admin/scholarship-summary', { params: { view_by: viewBy } }),
  getTargetAlerts: () => api.get('/admin/target-alerts'),
};

// Role-based Performance API (Team Lead, Admission Manager, Admin)
export const performanceAPI = {
  getStats: (params) => api.get('/performance/stats', { params }),
  getAdmissions: (params) => api.get('/performance/admissions', { params }),
};

// Target Tracking API
export const targetsAPI = {
  getAll: (params) => api.get('/targets', { params }),
  getCounselors: () => api.get('/targets/counselors'),
  getProgress: (period) => api.get('/targets/progress', { params: { period } }),
  create: (data) => api.post('/targets', data),
  update: (id, data) => api.put(`/targets/${id}`, data),
  delete: (id) => api.delete(`/targets/${id}`),
};

// Seed API
export const seedAPI = {
  seed: () => api.post('/seed'),
};

export default api;

// Placement Stats API (Admin)
export const placementStatsAPI = {
  getAll: (search) => api.get('/admin/placement-stats', { params: { search } }),
  getByCollege: (collegeId) => api.get(`/colleges/${collegeId}/placement-stats`),
  update: (collegeId, data) => api.put(`/colleges/${collegeId}/placement-stats`, data),
};

// Alerts API (Admin)
export const alertsAPI = {
  getTargetAlerts: () => api.get('/admin/target-alerts'),
  generateTargetAlerts: () => api.post('/notifications/generate-target-alerts'),
  sendCustomAlert: (data) => api.post('/notifications/custom-alert', data),
};

// Notifications API (for all users)
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
};

// ---- restored ----
export const consultationChargesAPI = {
  list: (params) => api.get('/consultation-charges', { params }),
  matrix: () => api.get('/consultation-charges/matrix'),
  bulkUpsert: (items) => api.put('/consultation-charges', { items }),
  gateStatus: () => api.get('/consultation-charges/gate/status'),
  gateUnlock: (password) => api.post('/consultation-charges/gate/unlock', { password }),
  gateSetPassword: (data) => api.put('/consultation-charges/gate/password', data),
};


export const incentiveAPI = {
  getConfig: () => api.get('/incentive-config'),
  setConfig: (data) => api.put('/incentive-config', data),
  mine: (params) => api.get('/incentives/me', { params }),
  counselor: (id, params) => api.get(`/incentives/counselor/${id}`, { params }),
  team: (params) => api.get('/incentives/team', { params }),
  finalize: (params) => api.post('/incentives/finalize', null, { params }),
  reports: () => api.get('/incentives/reports'),
};


export const incentiveCenterAPI = {
  status: () => api.get('/incentive-center/status'),
  unlock: (password) => api.post('/incentive-center/unlock', { password }),
  setPassword: (data) => api.put('/incentive-center/password', data),
};

export const leadDashboardsAPI = {
  counselor: (params) => api.get('/lead-dashboards/counselor', { params }),
  teamLead: (params) => api.get('/lead-dashboards/team-lead', { params }),
  admin: (params) => api.get('/lead-dashboards/admin', { params }),
  adminInsights: (params) => api.get('/lead-dashboards/admin-insights', { params }),
};


export const leadsAPI = {
  list: (params) => api.get('/leads', { params }),
  get: (id) => api.get(`/leads/${id}`),
  create: (data) => api.post('/leads', data),
  update: (id, data) => api.put(`/leads/${id}`, data),
  remove: (id) => api.delete(`/leads/${id}`),
  addRemark: (id, text) => api.post(`/leads/${id}/remarks`, { text }),
  activity: (id) => api.get(`/leads/${id}/activity`),
  exportCSV: () => api.get('/leads/export', { responseType: 'blob' }),
  featuredColleges: () => api.get('/leads/master/featured-colleges'),
  collegeCourses: (collegeIds) => api.get('/leads/master/college-courses', { params: { college_ids: (collegeIds || []).join(',') } }),
  whatsappFees:   (id, params) => api.get(`/leads/${id}/whatsapp-fees`, { params }),
};

export const adminAnalyticsAPI = {
  incentives: (params) => api.get('/admin/analytics/incentives', { params }),
  leads:      (params) => api.get('/admin/analytics/leads',      { params }),
};

export const myAnalyticsAPI = {
  leads: (params) => api.get('/my/analytics/leads', { params }),
};

export const actionPlanAPI = {
  mine:       ()       => api.get('/my/action-plan'),
  requestAI:  ()       => api.post('/my/ai-coaching/request'),
  latestAI:   ()       => api.get('/my/ai-coaching/latest'),
};

export const adminCoachingAPI = {
  list:        (params) => api.get('/admin/ai-coaching/requests', { params }),
  byCounselor: ()       => api.get('/admin/ai-coaching/by-counselor'),
  approve:     (id)     => api.post(`/admin/ai-coaching/requests/${id}/approve`),
  reject:      (id)     => api.post(`/admin/ai-coaching/requests/${id}/reject`),
  // coach-regen-v1 — admin force-regenerate (single counselor or bulk-missing)
  regenerate:        (counselorId) => api.post(`/admin/ai-coaching/regenerate/${counselorId}`),
  regenerateMissing: (params)      => api.post('/admin/ai-coaching/regenerate-missing', null, { params }),
};

// ─── Choice Entry Simulator — Incentive Module ──────────────────────────
// Backed by /var/www/counselor.ohcampus.com/backend/simulator_incentives.py.
// Endpoints are role-gated server-side; calls are safe to attempt and
// will return 403 for users out of scope.
export const simulatorIncentiveAPI = {
  // config (admin only on PUT, anyone authenticated on GET)
  getConfig:       ()                  => api.get('/simulator-incentives/config'),
  setConfig:       (data)              => api.post('/simulator-incentives/config', data),

  // dashboards
  meDashboard:     ()                  => api.get('/simulator-incentives/me/dashboard'),
  teamDashboard:   ()                  => api.get('/simulator-incentives/team/dashboard'),
  adminDashboard:  ()                  => api.get('/simulator-incentives/admin/dashboard'),

  // lists
  listSignups:     (params)            => api.get('/simulator-incentives/signups', { params }),
  listPayouts:     (params)            => api.get('/simulator-incentives/payouts', { params }),
  auditLog:        (params)            => api.get('/simulator-incentives/audit-log', { params }),

  // admin actions
  syncSignups:     ()                  => api.post('/simulator-incentives/sync-signups'),
  recalculate:     ()                  => api.post('/simulator-incentives/admin/recalculate'),
  reassignSignup:  (id, body)          => api.post(`/simulator-incentives/admin/signups/${id}/reassign`, body),
  approvePayout:   (id, body)          => api.post(`/simulator-incentives/admin/payouts/${id}/approve`, body),
  rejectPayout:    (id, body)          => api.post(`/simulator-incentives/admin/payouts/${id}/reject`, body),
  releasePayout:   (id, body)          => api.post(`/simulator-incentives/admin/payouts/${id}/release`, body),
  bulkRelease:     (body)              => api.post('/simulator-incentives/admin/payouts/bulk-release', body),

  // exports — must use blob responseType so the browser downloads cleanly
  exportUrl:       (params = {})       => {
    const qs = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/simulator-incentives/export?${qs}`;
  },
  exportBlob:      (params = {})       => api.get('/simulator-incentives/export', { params, responseType: 'blob' }),

  // leaderboard
  leaderboard:     (params = {})       => api.get('/simulator-incentives/leaderboard', { params }),
};


// ─── Simulator Lead Capture + Invitation ─────────────────────────────────
export const simulatorLeadsAPI = {
  create:    (data)            => api.post('/simulator-leads', data),
  list:      (params)          => api.get('/simulator-leads', { params }),
  remove:    (id)              => api.delete(`/simulator-leads/${id}`),
  whatsapp:  (id, params)      => api.get(`/simulator-leads/${id}/whatsapp`, { params }),
};

